import { Button } from "@components/common/button";
import { DictType } from "@pma/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog";
import axios from "axios";
import { parseCookies } from "nookies";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@hooks/use-toast";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { PROJECT_DELETE_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes";
import { Trash2Icon } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/common/tooltip";
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes";
import { HTMLAttributes } from "react";
import useTranslator from "@hooks/use-translator";

interface Props extends HTMLAttributes<HTMLDivElement> {
  project: DictType;
}

const DeleteProject = ({ project, children, className }: Props) => {
  const accessToken = parseCookies()[ACCESS_TOKEN];
  const navigate = useNavigate();
  const { translate } = useTranslator();

  const [searchParam] = useSearchParams();
  const newParams = new URLSearchParams(searchParam);

  const deleteProject = async () => {
    const response = await axios.delete(
      SERVER_URL + PROJECT_DELETE_ENDPOINT + `/${project?.id}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const deleteProjectMutation = useMutation({
    mutationKey: ["delete-project", project?.id],
    mutationFn: deleteProject,
    onSuccess: (data) => {
      if (children) {

        newParams.set('revalidate', 'projects');
        
        navigate(PROJECTS_PAGE_ROUTE + `?${newParams.toString()}`);

      } else {
        newParams.set('revalidate', 'projects');
        navigate(`?${newParams.toString()}`);
      }

      toast({
        title: translate("Success"),
        description: `${project?.title} ${translate("has been deleted")}`,
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: `${translate("Unable to delete")} ${project?.title} ${translate("project")}`,
        variant: "error",
      });
    },
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger className={className}>
        <Tooltip>
          <TooltipTrigger>
            {children ? (
              children
            ) : (
              <Button
                variant={"ghost"}
                className="text-xs text-red-500"
                size={"icon"}
              >
                <Trash2Icon size={14} />
              </Button>
            )}
          </TooltipTrigger>
          <TooltipContent className="flex bg-destructive items-center gap-1">
            <h4>
              {translate(`Delete`)} {project?.title}
            </h4>
          </TooltipContent>
        </Tooltip>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to delete")} {project?.title}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This action cannot be undone")}.{" "}
            {translate("This will permanently delete the project")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{translate("Cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive"
            disabled={deleteProjectMutation?.isPending}
            onClick={() => deleteProjectMutation.mutate()}
          >
            {translate("Continue")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteProject;
